// JournalSearchModal.js - Sök i journalinnehåll
import React, { useState, useEffect, useRef, useCallback } from 'react';
import apiClient from '../../../apiClient';
import './JournalSearchModal.css';

const JournalSearchModal = ({ onClose, onSelectEntry }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const [sortBy, setSortBy] = useState('date_desc');
  const [filters, setFilters] = useState({
    status: '',
    dateFrom: '',
    dateTo: ''
  });
  const searchInputRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  useEffect(() => {
    // Focus search input on mount
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }

    // Handle keyboard shortcuts
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const performSearch = useCallback(async (searchQuery, searchPage = 1) => {
    if (!searchQuery.trim()) {
      setResults([]);
      setTotalResults(0);
      setTotalPages(1);
      return;
    }

    try {
      setLoading(true);
      const params = {
        q: searchQuery,
        page: searchPage,
        per_page: 10,
        sort: sortBy
      };

      if (filters.status) params.status = filters.status;
      if (filters.dateFrom) params.date_from = filters.dateFrom;
      if (filters.dateTo) params.date_to = filters.dateTo;

      const response = await apiClient.get('/api/journal/search', { params });
      setResults(response.data.entries || []);
      setTotalResults(response.data.total || 0);
      setTotalPages(response.data.pages || 1);
      setPage(searchPage);
    } catch (error) {
      console.error('Search error:', error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [sortBy, filters]);

  // Debounced search
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      performSearch(query, 1);
    }, 300);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [query, performSearch]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const handleSortChange = (newSort) => {
    setSortBy(newSort);
    performSearch(query, 1);
  };

  const handlePageChange = (newPage) => {
    performSearch(query, newPage);
  };

  const highlightText = (text, searchQuery) => {
    if (!searchQuery.trim() || !text) return text;

    const regex = new RegExp(`(${searchQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);

    return parts.map((part, i) =>
      regex.test(part) ? <mark key={i}>{part}</mark> : part
    );
  };

  const getExcerpt = (content, searchQuery, maxLength = 200) => {
    if (!content) return '';

    // Find the search term position
    const lowerContent = content.toLowerCase();
    const lowerQuery = searchQuery.toLowerCase();
    const index = lowerContent.indexOf(lowerQuery);

    if (index === -1) {
      // Term not found, return start of content
      return content.substring(0, maxLength) + (content.length > maxLength ? '...' : '');
    }

    // Calculate excerpt around the found term
    const start = Math.max(0, index - 50);
    const end = Math.min(content.length, index + searchQuery.length + 100);

    let excerpt = content.substring(start, end);
    if (start > 0) excerpt = '...' + excerpt;
    if (end < content.length) excerpt = excerpt + '...';

    return excerpt;
  };

  return (
    <div className="journal-search-modal-overlay" onClick={onClose}>
      <div className="journal-search-modal" onClick={(e) => e.stopPropagation()}>
        <div className="journal-search-header">
          <div className="journal-search-input-wrapper">
            <i className="fas fa-search"></i>
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Sök i journalinnehåll..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            {query && (
              <button
                className="search-close-btn"
                onClick={() => setQuery('')}
              >
                <i className="fas fa-times"></i>
              </button>
            )}
          </div>

          <div className="journal-search-filters">
            <div className="search-filter">
              <label>Status:</label>
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <option value="">Alla</option>
                <option value="draft">Utkast</option>
                <option value="signed">Signerade</option>
              </select>
            </div>

            <div className="search-filter">
              <label>Från:</label>
              <input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
              />
            </div>

            <div className="search-filter">
              <label>Till:</label>
              <input
                type="date"
                value={filters.dateTo}
                onChange={(e) => handleFilterChange('dateTo', e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="journal-search-results">
          {loading ? (
            <div className="search-loading">
              <i className="fas fa-spinner fa-spin"></i>
              Söker...
            </div>
          ) : !query.trim() ? (
            <div className="search-empty">
              <i className="fas fa-search"></i>
              <h4>Sök i journaler</h4>
              <p>Skriv sökord för att hitta journalanteckningar</p>
            </div>
          ) : results.length === 0 ? (
            <div className="search-empty">
              <i className="fas fa-folder-open"></i>
              <h4>Inga resultat</h4>
              <p>Inga journaler matchade "{query}"</p>
            </div>
          ) : (
            <>
              <div className="search-results-header">
                <span className="search-results-count">
                  {totalResults} resultat för "{query}"
                </span>
                <div className="search-results-sort">
                  <label>Sortera:</label>
                  <select value={sortBy} onChange={(e) => handleSortChange(e.target.value)}>
                    <option value="date_desc">Datum (nyast först)</option>
                    <option value="date_asc">Datum (äldst först)</option>
                    <option value="relevance">Relevans</option>
                  </select>
                </div>
              </div>

              {results.map((entry) => (
                <div
                  key={entry.entry_id}
                  className="search-result-item"
                  onClick={() => onSelectEntry(entry)}
                >
                  <div className="search-result-header">
                    <span className="search-result-patient">
                      {entry.patient_name || 'Okänd patient'}
                    </span>
                    <span className="search-result-date">
                      {new Date(entry.entry_date).toLocaleDateString('sv-SE')}
                    </span>
                  </div>
                  <div className="search-result-meta">
                    <span>
                      <i className="fas fa-file-alt"></i>
                      {entry.template_name || 'Ingen mall'}
                    </span>
                    <span className={entry.status === 'signed' ? 'status-signed' : 'status-draft'}>
                      <i className={`fas ${entry.status === 'signed' ? 'fa-check-circle' : 'fa-edit'}`}></i>
                      {entry.status === 'signed' ? 'Signerad' : 'Utkast'}
                    </span>
                    {entry.user_name && (
                      <span>
                        <i className="fas fa-user"></i>
                        {entry.user_name}
                      </span>
                    )}
                  </div>
                  <div className="search-result-excerpt">
                    {highlightText(
                      getExcerpt(entry.content_preview || entry.raw_content, query),
                      query
                    )}
                  </div>
                </div>
              ))}
            </>
          )}
        </div>

        {totalPages > 1 && (
          <div className="search-pagination">
            <button
              onClick={() => handlePageChange(page - 1)}
              disabled={page <= 1}
            >
              <i className="fas fa-chevron-left"></i> Föregående
            </button>
            <span>Sida {page} av {totalPages}</span>
            <button
              onClick={() => handlePageChange(page + 1)}
              disabled={page >= totalPages}
            >
              Nästa <i className="fas fa-chevron-right"></i>
            </button>
          </div>
        )}

        <div className="search-keyboard-hint">
          <div className="keyboard-hint-item">
            <kbd>Esc</kbd> Stäng
          </div>
          <div className="keyboard-hint-item">
            <kbd>Enter</kbd> Öppna första resultat
          </div>
        </div>
      </div>
    </div>
  );
};

export default JournalSearchModal;
